import React, { useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Table, TableHead, TableRow, TableCell, TableBody, Chip, Divider } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { PayoffDiagram } from '../../components/options/PayoffDiagram';
import { useOptionsStore } from '../../store/useOptionsStore';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const ActivePositionsPage: React.FC = () => {
  const { activePosition, spotData, fetchPositions, fetchSpotData } = useOptionsStore();
  const spot = spotData?.spot || 23234.6;

  useEffect(() => {
    fetchPositions();
    fetchSpotData();
    const interval = setInterval(() => {
      fetchPositions();
      fetchSpotData();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const netDelta = activePosition?.greeks?.delta ?? 0;
  const isAdjustmentNeeded = Math.abs(netDelta) >= 0.22;

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* Active Multi-Leg Card */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalanceWalletIcon sx={{ color: '#0284C7' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                  {activePosition ? `${activePosition.strategyName.replace('_', ' ')} #${activePosition.id}` : 'ACTIVE POSITIONS'}
                </Typography>
              </Stack>
              {activePosition && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`OPEN • ${activePosition.expiry}`} sx={{ bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#16A34A', fontWeight: 700 }} />
                  <Chip size="small" label={activePosition.openTime} sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600, fontSize: '0.7rem' }} />
                </Stack>
              )}
            </Stack>

            {activePosition ? (
              <>
                {/* Legs Table */}
                <Table size="small" sx={{ mb: 2, fontFamily: "'Roboto Mono', monospace" }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { color: '#64748B', fontWeight: 600, fontSize: '0.72rem' } }}>
                      <TableCell>LEG</TableCell>
                      <TableCell>SIDE</TableCell>
                      <TableCell align="right">STRIKE</TableCell>
                      <TableCell align="right">ENTRY</TableCell>
                      <TableCell align="right">LTP</TableCell>
                      <TableCell align="right">P&L</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activePosition.legs.map((leg) => (
                      <TableRow key={leg.legId} sx={{ '& td': { fontSize: '0.78rem' } }}>
                        <TableCell sx={{ fontWeight: 700, color: '#0284C7' }}>{leg.optionType}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={leg.side}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: leg.side === 'SELL' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                              color: leg.side === 'SELL' ? '#DC2626' : '#16A34A',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {leg.strike}
                        </TableCell>
                        <TableCell align="right">₹{leg.entryPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">₹{leg.currentPrice.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: leg.pnl >= 0 ? '#16A34A' : '#DC2626' }}>
                          {leg.pnl >= 0 ? '+' : ''}₹{leg.pnl.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Greek Aggregation Grid */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 3 }}>
                    <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Net Delta
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284C7' }}>
                        {activePosition.greeks.delta >= 0 ? '+' : ''}{activePosition.greeks.delta}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Net Gamma
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {activePosition.greeks.gamma}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Theta Decay
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                        {activePosition.greeks.theta >= 0 ? '+' : ''}₹{activePosition.greeks.theta}/d
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Net Vega
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#DC2626' }}>
                        -₹{Math.abs(activePosition.greeks.vega)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Dynamic Adjustment State */}
                <Paper elevation={0} sx={{ p: 1.5, bgcolor: isAdjustmentNeeded ? '#FFFBEB' : '#F0FDF4', border: `1px solid ${isAdjustmentNeeded ? '#FCD34D' : '#BBF7D0'}`, borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AutoFixHighIcon sx={{ color: isAdjustmentNeeded ? '#D97706' : '#16A34A', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isAdjustmentNeeded ? '#B45309' : '#166534' }}>
                      Dynamic Adjustment Engine: {isAdjustmentNeeded ? `WATCH (Delta: ${netDelta >= 0 ? '+' : ''}${netDelta} approaching ±0.30)` : `STANDBY (Delta: ${netDelta >= 0 ? '+' : ''}${netDelta} within tolerance ±0.30)`}
                    </Typography>
                  </Stack>
                </Paper>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: '#94A3B8', py: 4, textAlign: 'center' }}>
                No active multi-leg position.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Payoff Diagram */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <PayoffDiagram spot={spot} currentPnl={activePosition?.unrealizedPnl ?? 0} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivePositionsPage;
